import { useState, type ReactNode } from "react";

import type { SpecType } from "@/api/contracts/specifications";
import { MuiAddIcon } from "@/components/ui/mui-community-icons";
import { MuiArrowDropDownIcon } from "@/components/ui/mui-arrow-icons";
import { MuiButton } from "@/components/ui/mui-button";
import { MuiArrowDropUpIcon, MuiDehazeIcon } from "@/components/ui/mui-spec-icons";
import type { SpecEditorTarget } from "@/hooks/spec-editor-target";
import type { SpecGroupExpansion } from "@/hooks/spec-lines";
import type { SpecLinesGroup } from "@/hooks/specifications";

import { SpecGroup, type SpecGroupMemoryProp } from "./spec-group";
import { SpecGroupActions } from "./spec-group-dialogs";

// One group in the Editor — the donor's `EditableGroup` / `EditGroupRows`
// (`SpecEditIsland/components/MainContent/Groups/EditableGroup.tsx`) — drawn
// through the details page's card and its slots (`spec-group.tsx`), not a fork
// of it (#805). What the Editor's card adds:
//
//   - **the hover handle.** `onMouseEnter` / `onMouseLeave` on the header row
//     only (`:133-135`). Hovered, a 16px `#D0CFCF` `Dehaze` stands first in
//     the row, the row's left padding drops 16 → 0 and its gap 8 → 4 — at
//     once, no transition. The donor draws a second `Dehaze` there, absolute at
//     `top: 30px`, but it sits *before* the card in an unpositioned box, so it
//     resolves against the page: measured on the running donor at the page's
//     (72, 30), under the app bar's logo, where no user sees it. Not drawn — a
//     named parity break for the owner (#805 enumeration B4);
//   - **the Editor's chevron** — `ArrowDropUp` open, `ArrowDropDown` closed;
//   - **the ⋮** after the name column (`SpecGroupActions`);
//   - **a bare body** — the grid's line list, with no head row and no "0
//     lines" row;
//   - **`Add specification line`** at the foot of the collapse
//     (`AddLineToGroup.tsx:97-106`): a `py 16 px 24` box round a primary text
//     button with the `Add` start icon. The donor renders it as a `span`
//     (`component='span'`), which rbd does not count as interactive, so a press
//     there that moves 5px lifts the whole group — kept: it is a `span` with
//     the button role and keys MUI's `ButtonBase` gives one;
//   - **a header that does not stick while its card is in the air**, so the
//     lifted card stays whole;
//   - **a copy in the air that is the card as it stood** — its fold and its
//     note's More / Less carried over (`memory`), where the donor lifts the
//     very node — and that repeats none of the slot's ids;
//   - **the group flag's header** (#1104; #1060 Q5, Q8; ledger §7, "Changed
//     from base widens"), an Atlas addition the donor's header never draws:
//     with "Show changes from Base" on, a flagged group's header shows the
//     details page's marks — the tint, the icon, the name and notes pairs —
//     in the details page's changed tint on either spec type (owner
//     disposition D1 = b on PR #1109), at every width, and the copy in the
//     air draws them as the card stood.
//
// Its expansion is the read side's: groups open by default, the 300ms write
// back to the map, and the Expand / Collapse all pulses (`useGroupExpansion`).

export interface EditableSpecGroupProps {
  group: SpecLinesGroup;
  expansion: SpecGroupExpansion;
  target: SpecEditorTarget;
  /** The group's lines — the grid's drop list for this group. */
  lines: ReactNode;
  /** The copy in the air rather than the card in its slot. */
  lifted?: boolean;
  /** `Add specification line` — the dialog it opens is #807's. */
  onAddLine: () => void;
  /** The card's fold and note, kept across the copy drawn in the air. */
  memory?: SpecGroupMemoryProp;
  /** The spec's type: the header's sentence (#1104). */
  specType?: SpecType;
  /** "Show changes from Base". */
  showChanges?: boolean;
}

export function EditableSpecGroup({
  group,
  expansion,
  target,
  lines,
  lifted = false,
  onAddLine,
  memory,
  specType,
  showChanges = false,
}: EditableSpecGroupProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <SpecGroup
      group={group}
      expansion={expansion}
      sticky={!lifted}
      memory={memory}
      baseChanges={showChanges && specType ? { specType, group } : undefined}
      header={{
        className: hovered ? "gap-1 pl-0" : undefined,
        onMouseEnter: () => setHovered(true),
        onMouseLeave: () => setHovered(false),
      }}
      leading={hovered ? <MuiDehazeIcon className="text-spec-drag-mark" /> : null}
      chevron={{ open: <MuiArrowDropUpIcon />, closed: <MuiArrowDropDownIcon /> }}
      trailing={
        <SpecGroupActions group={group} target={target} lifted={lifted} specType={specType} />
      }
      body={lines}
      footer={
        <div className="px-6 py-4">
          <MuiButton asChild startIcon={<MuiAddIcon />}>
            <span
              role="button"
              tabIndex={0}
              onClick={onAddLine}
              // `ButtonBase` on a non-button: Enter clicks on the way down and
              // Space on the way up. Its Space keydown is left undefaulted
              // here: in the donor rbd's window-capture sensor takes that key
              // before `ButtonBase` does and lifts the group, and `@dnd-kit`
              // ignores a key that arrives defaulted — the card's sensor
              // defaults it as it lifts.
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  onAddLine();
                }
              }}
              onKeyUp={(event) => {
                if (event.key === " " && !event.defaultPrevented) onAddLine();
              }}
            >
              Add specification line
            </span>
          </MuiButton>
        </div>
      }
    />
  );
}
